import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';

/**
 * Send OTP request (Email)
 */
export interface SendEmailOtpRequest {
    commType: 'Email';
    email: string;
}

/**
 * Send OTP request (SMS)
 */
export interface SendSmsOtpRequest {
    commType: 'SMS';
    country_code: string;
    mobileNumber: string;
}
export type SendOtpRequest = SendEmailOtpRequest | SendSmsOtpRequest;

/**
 * Send OTP response
 */
export interface SendOtpResponse {
    status: 'success';
    details: {
        message: string;
        transactionId: string;
    };
}

/**
 * Send OTP error response
 */
export interface SendOtpErrorResponse {
    error: string;
}

/**
 * Verify OTP request
 */
export interface VerifyOtpRequest {
    otp: string;
    transactionId: string;
    update_type: 'Primary' | 'Secondary';
    country_code?: string; // Required for phone OTP
    mobileNumber?: string; // Required for phone OTP (with country code included)
    email?: string; // Required for email OTP
}
/**
 * Verify OTP response
 */
export interface VerifyOtpResponse {
    status: 'success';
    details: {
        message: string;
    };
}

/**
 * Verify OTP error response
 */
export interface VerifyOtpErrorResponse {
    error: string;
}

/**
 * Verify OTP for registration request
 */
export interface VerifyOtpRegistrationRequest {
    otp: string;
    transactionId: string;
    email?: string;
    country_code?: string; 
    mobileNumber?: string; 
}

/**
 * Verify OTP for registration response (same structure as VerifyOtpResponse)
 */
export interface VerifyOtpRegistrationResponse {
    status: 'success';
    details: {
        message: string;
    };
}

/**
 * OTP Service
 */
class OtpService {
    /**
     * Send OTP via Email or SMS
     */
    async sendOtp(request: SendOtpRequest): Promise<SendOtpResponse> {
        try {
            logger.debug('OtpService:Sending OTP', { commType: request.commType });
            const response = await apiClient.post<SendOtpResponse>(
                Endpoints.registration.sendOtp as string,
                request
            );
            logger.info('OtpService:OTP sent successfully', {
                transactionId: response.details.transactionId,
            });
            return response;
        } catch (error: any) {
            logger.error('OtpService:Failed to send OTP', error);
            throw error;
        }
    }

    /**
     * Verify OTP
     */
    async verifyOtp(request: VerifyOtpRequest): Promise<VerifyOtpResponse> {
        try {
            logger.debug('OtpService:Verifying OTP', { transactionId: request.transactionId });
            const response = await apiClient.post<VerifyOtpResponse>(
                Endpoints.registration.verifyOtp as string,
                request
            );

            // Check if verification was successful based on message
            const isVerified = response.details.message.toLowerCase().includes('successfully');
            logger.info('OtpService:OTP verification result', {
                transactionId: request.transactionId,
                isVerified,
                message: response.details.message,
            });

            return response;
        } catch (error: any) {
            logger.error('OtpService:Failed to verify OTP', error);
            throw error;
        }
    }

    /**
     * Verify OTP for registration (simpler endpoint - no customer_id, update_type, etc.)
     */
    async verifyOtpRegistration(request: VerifyOtpRegistrationRequest): Promise<VerifyOtpRegistrationResponse> {
        try {
            logger.debug('OtpService:Verifying OTP for registration', { transactionId: request.transactionId });
            const response = await apiClient.post<VerifyOtpRegistrationResponse>(
                Endpoints.registration.verifyOtpRegistration as string,
                request
            );

            // Check if verification was successful based on message
            const isVerified = response.details.message.toLowerCase().includes('successfully');
            logger.info('OtpService:OTP registration verification result', {
                transactionId: request.transactionId,
                isVerified,
                message: response.details.message,
            });

            return response;
        } catch (error: any) {
            logger.error('OtpService:Failed to verify OTP for registration', error);
            throw error;
        }
    }
}

export const otpService = new OtpService();

